import React, { useState, useEffect } from 'react';
import { Thermometer, Zap, Battery, MapPin, Download, Eye, Wifi, Settings, Globe, Clock } from 'lucide-react';
import { Device } from '../types';
import DeviceMap from './DeviceMap';
import {
  SensorPanelsContainer,
  LeftColumn,
  RightColumn,
  SensorPanel,
  PanelHeader,
  PanelIcon,
  PanelTitle,
  SensorList,
  SensorRow,
  SensorLabel,
  SensorValue,
  MapContainer,
  TrackingInfo,
  TrackingLabel,
  TrackingValue,
  MapActions,
  MapButton,
  ExportButton
} from '../styles/SensorPanelStyles';

interface SensorPanelsProps {
  device: Device;
}

// Use relative URL in development to leverage the proxy
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_BASE_URL || '')
  : '';

const SensorPanels: React.FC<SensorPanelsProps> = ({ device }) => {
  const [latestData, setLatestData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/devices/${device.id}/latest`);
        if (response.ok) {
          const data = await response.json();
          setLatestData(data.data); // Extract the decoded_json data
        }
      } catch (error) {
        console.error('Error fetching latest data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestData();
    // Refresh data every 30 seconds
    const interval = setInterval(fetchLatestData, 30000);
    return () => clearInterval(interval);
  }, [device.id]);

  const getSensorStatus = (value: number | undefined, normalRange: [number, number]): 'normal' | 'warning' | 'critical' | 'inactive' => {
    if (value === undefined || value === null || value === -300) return 'inactive';
    if (value < normalRange[0] * 0.8 || value > normalRange[1] * 1.2) return 'critical';
    if (value < normalRange[0] || value > normalRange[1]) return 'warning';
    return 'normal';
  };

  const getSignalStatus = (signalStrength: number | undefined): 'normal' | 'warning' | 'critical' | 'inactive' => {
    if (signalStrength === undefined || signalStrength === 99) return 'inactive';
    if (signalStrength === 0) return 'critical';
    if (signalStrength >= 15) return 'normal';
    if (signalStrength >= 10) return 'warning';
    return 'critical';
  };

  const formatValue = (value: any, unit: string = '', decimals: number = 1): string => {
    if (value === undefined || value === null || value === -300) return '--';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '--';
    return `${numValue.toFixed(decimals)}${unit}`;
  };

  const formatCoordinates = (coord: number | undefined, decimals: number = 6): string => {
    return coord !== undefined ? `${coord.toFixed(decimals)}` : '0.000000';
  };

  // Extract data from latest message
  // Helper to safely parse numeric values from strings
  const parseValue = (value: any): number | undefined => {
    if (value === undefined || value === null) return undefined;
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(parsed) ? undefined : parsed;
  };

  const temp = parseValue(latestData?.temperature);
  const housingTemp = parseValue(latestData?.housing_peripheral_edge_temperature);
  const internalSeaTemp = parseValue(latestData?.internal_sea_temperature);
  const externalSeaTemp = parseValue(latestData?.external_sea_temperature);
  const spareTemp = parseValue(latestData?.spare_temperature);
  const boardTemp = parseValue(latestData?.board_temperature);
  const batteryTemp = parseValue(latestData?.battery_temperature);

  const solarVoltage = parseValue(latestData?.solar_panel_voltage);
  const solarCurrent = parseValue(latestData?.solar_panel_current);
  const batteryVoltage = parseValue(latestData?.battery_voltage);
  const batteryCurrent = parseValue(latestData?.battery_current);
  const batteryCapacity = parseValue(latestData?.battery_capacity);
  const batteryFill = parseValue(latestData?.battery_fill);
  const batteryRelativeFill = parseValue(latestData?.battery_relative_fill);

  const significantWaveHeight = parseValue(latestData?.significant_wave_height);
  const maxWaveHeight = parseValue(latestData?.maximum_wave_height);
  const peakWavePeriod = parseValue(latestData?.peak_wave_period);
  const meanWavePeriod = parseValue(latestData?.mean_wave_period);
  const peakWaveDirection = parseValue(latestData?.peak_wave_direction);
  const avgWaveHeight = parseValue(latestData?.average_wave_height);

  const spectrumSignificantWaveHeight = parseValue(latestData?.spectrum_significant_wave_height);
  const spectrumMaxWaveHeight = parseValue(latestData?.spectrum_maximum_wave_height);
  const spectrumPeakWavePeriod = parseValue(latestData?.spectrum_peak_wave_period);
  const spectrumMeanWavePeriod = parseValue(latestData?.spectrum_mean_wave_period);
  const spectrumPeakWaveDirection = parseValue(latestData?.spectrum_peak_wave_direction);
  const spectrumAvgWaveHeight = parseValue(latestData?.spectrum_average_wave_height);

  const rtcBatteryVoltage = parseValue(latestData?.rtc_battery_voltage);
  const softwareVersion = latestData?.software_version || '--';
  const processorVoltage = parseValue(latestData?.microchip_regulated_voltage);
  const signalStrength = parseValue(latestData?.signal_strength) ?? device.signalStrength;

  const thermometryData = [
    { label: 'Temperature (°C)', value: formatValue(temp, '°C'), status: getSensorStatus(temp, [15, 30]) },
    { label: 'Solar Housing Peripheral Edge Temperature (ID 0) (LOW RES)', value: formatValue(housingTemp, '°C'), status: getSensorStatus(housingTemp, [10, 50]) },
    { label: 'Sea Temperature (Potted Core) (ID 1) (HIGH RES)', value: formatValue(internalSeaTemp, '°C'), status: getSensorStatus(internalSeaTemp, [0, 40]) },
    { label: 'External Sea Temperature (ID 2) (HIGH RES)', value: formatValue(externalSeaTemp, '°C'), status: getSensorStatus(externalSeaTemp, [0, 40]) },
    { label: 'Spare Temperature (ID3) (HIGH RES)', value: formatValue(spareTemp, '°C'), status: getSensorStatus(spareTemp, [0, 40]) },
    { label: 'AquaSense 100 Board Temperature (ID 4) (LOW RES)', value: formatValue(boardTemp, '°C'), status: getSensorStatus(boardTemp, [10, 50]) },
    { label: 'APT Solar PCB Battery Temperature', value: formatValue(batteryTemp, '°C'), status: getSensorStatus(batteryTemp, [0, 50]) },
  ];

  const electricalData = [
    { label: 'APT Solar PCB Panel Voltage', value: formatValue(solarVoltage, 'mV', 0), status: getSensorStatus(solarVoltage, [3000, 8000]) },
    { label: 'APT Solar PCB Panel Current', value: formatValue(solarCurrent, 'mA', 0), status: solarCurrent !== undefined && solarCurrent > 0 ? 'normal' as const : 'inactive' as const },
    { label: 'APT Solar PCB Battery Voltage', value: formatValue(batteryVoltage, 'mV', 0), status: getSensorStatus(batteryVoltage, [3500, 4200]) },
    { label: 'APT Solar PCB Battery Current', value: formatValue(batteryCurrent, 'mA', 0), status: 'normal' as const },
    { label: 'APT Solar PCB Battery Capacity', value: formatValue(batteryCapacity, 'mAh', 0), status: getSensorStatus(batteryCapacity, [1000, 5000]) },
    { label: 'APT Solar PCB Battery Fill', value: formatValue(batteryFill, 'mAh', 0), status: getSensorStatus(batteryFill, [10000, 40000]) },
    { label: 'APT Solar PCB Battery Rel. Fill', value: formatValue(batteryRelativeFill, '%', 0), status: getSensorStatus(batteryRelativeFill, [20, 100]) },
  ];

  const waveData = [
    { label: 'Significant Wave Height (Hs)', value: formatValue(significantWaveHeight, ' M', 2), status: getSensorStatus(significantWaveHeight, [0, 10]) },
    { label: 'Maximum Wave Height (Hmax)', value: formatValue(maxWaveHeight, ' M', 2), status: getSensorStatus(maxWaveHeight, [0, 15]) },
    { label: 'Peak Wave Period (Tp)', value: formatValue(peakWavePeriod, ' S', 1), status: getSensorStatus(peakWavePeriod, [0, 20]) },
    { label: 'Mean Wave Period (Tm)', value: formatValue(meanWavePeriod, ' S', 1), status: getSensorStatus(meanWavePeriod, [0, 20]) },
    { label: 'Peak Frequency Wave Direction (Dirp)', value: formatValue(peakWaveDirection, '°', 0), status: peakWaveDirection !== undefined ? 'normal' as const : 'inactive' as const },
    { label: 'Average Wave Height (Hav)', value: formatValue(avgWaveHeight, ' M', 2), status: getSensorStatus(avgWaveHeight, [0, 10]) },
  ];

  const spectrumData = [
    { label: 'Spectrum - Signficant Wave Height (Hs)', value: formatValue(spectrumSignificantWaveHeight, ' m', 2), status: getSensorStatus(spectrumSignificantWaveHeight, [0, 10]) },
    { label: 'Spectrum - Maximum Wave Height (Hmax)', value: formatValue(spectrumMaxWaveHeight, ' m', 2), status: getSensorStatus(spectrumMaxWaveHeight, [0, 15]) },
    { label: 'Spectrum - Peak Wave Period (Tp)', value: formatValue(spectrumPeakWavePeriod, ' s', 1), status: getSensorStatus(spectrumPeakWavePeriod, [0, 20]) },
    { label: 'Spectrum - Mean Wave Period (Tav)', value: formatValue(spectrumMeanWavePeriod, ' s', 1), status: getSensorStatus(spectrumMeanWavePeriod, [0, 20]) },
    { label: 'Spectrum - Peak Wave Direction (Dirp)', value: formatValue(spectrumPeakWaveDirection, '°', 0), status: spectrumPeakWaveDirection !== undefined ? 'normal' as const : 'inactive' as const },
    { label: 'Spectrum - Wave Height Average (Havg)', value: formatValue(spectrumAvgWaveHeight, ' m', 2), status: getSensorStatus(spectrumAvgWaveHeight, [0, 10]) },
  ];

  const batteryData = [
    { label: 'RTC Battery Voltage (V)', value: formatValue(rtcBatteryVoltage, 'V', 1), status: getSensorStatus(rtcBatteryVoltage, [2.5, 3.5]) },
  ];

  const geospatialData = [
    { label: 'GPS Latitude', value: `${formatCoordinates(device.latitude)} Decimal Degrees`, status: device.latitude ? 'normal' as const : 'inactive' as const },
    { label: 'GPS Longitude', value: `${formatCoordinates(device.longitude)} Decimal Degrees`, status: device.longitude ? 'normal' as const : 'inactive' as const },
  ];

  const systemData = [
    { label: 'Software Version', value: softwareVersion, status: 'normal' as const },
    { label: 'Processor Regulated Voltage (V)', value: formatValue(processorVoltage, 'V', 1), status: getSensorStatus(processorVoltage, [3.0, 5.0]) },
  ];

  const communicationData = [
    { label: 'Iridium Signal Strength', value: `${signalStrength ?? '--'}/31`, status: getSignalStatus(signalStrength) },
  ];

  const miscData = [
    { label: 'Time', value: latestData?.time || '--', status: 'normal' as const },
    { label: 'Date', value: latestData?.date || '--', status: 'normal' as const },
  ];

  if (loading) {
    return (
      <SensorPanelsContainer>
        <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
          Loading sensor data...
        </div>
      </SensorPanelsContainer>
    );
  }

  return (
    <>
      <SensorPanelsContainer>
        <LeftColumn>

          {/* Thermometry */}
          <SensorPanel>
            <PanelHeader>
              <PanelIcon>
                <Thermometer size={20} />
              </PanelIcon>
              <PanelTitle>Thermometry</PanelTitle>
            </PanelHeader>
            <SensorList>
              {thermometryData.map((sensor, index) => (
                <SensorRow key={index}>
                  <SensorLabel>{sensor.label}</SensorLabel>
                  <SensorValue status={sensor.status}>{sensor.value}</SensorValue>
                </SensorRow>
              ))}
            </SensorList>
          </SensorPanel>

          {/* Electrical */}
          <SensorPanel>
            <PanelHeader>
              <PanelIcon>
                <Zap size={20} />
              </PanelIcon>
              <PanelTitle>Electrical</PanelTitle>
            </PanelHeader>
            <SensorList>
              {electricalData.map((sensor, index) => (
                <SensorRow key={index}>
                  <SensorLabel>{sensor.label}</SensorLabel>
                  <SensorValue status={sensor.status}>{sensor.value}</SensorValue>
                </SensorRow>
              ))}
            </SensorList>
          </SensorPanel>

          {/* Wave Measurements */}
          <SensorPanel>
            <PanelHeader>
              <PanelIcon>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 12c0-2.8 2.2-5 5-5s5 2.2 5 5-2.2 5-5 5-5-2.2-5-5z"/>
                  <path d="M12 12c0-2.8 2.2-5 5-5s5 2.2 5 5-2.2 5-5 5-5-2.2-5-5z"/>
                </svg>
              </PanelIcon>
              <PanelTitle>Wave</PanelTitle>
            </PanelHeader>
            <SensorList>
              {waveData.map((sensor, index) => (
                <SensorRow key={index}>
                  <SensorLabel>{sensor.label}</SensorLabel>
                  <SensorValue status={sensor.status}>{sensor.value}</SensorValue>
                </SensorRow>
              ))}
            </SensorList>
          </SensorPanel>

          {/* Spectrum Analysis */}
          <SensorPanel>
            <PanelHeader>
              <PanelIcon>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18"/>
                  <path d="m19 9-5 5-4-4-3 3"/>
                </svg>
              </PanelIcon>
              <PanelTitle>Spectrum Analysis</PanelTitle>
            </PanelHeader>
            <SensorList>
              {spectrumData.map((sensor, index) => (
                <SensorRow key={index}>
                  <SensorLabel>{sensor.label}</SensorLabel>
                  <SensorValue status={sensor.status}>{sensor.value}</SensorValue>
                </SensorRow>
              ))}
            </SensorList>
          </SensorPanel>
        </LeftColumn>

        <RightColumn>
          {/* Location Map */}
          <SensorPanel>
            <PanelHeader>
              <PanelIcon>
                <MapPin size={20} />
              </PanelIcon>
              <PanelTitle>Device Location</PanelTitle>
            </PanelHeader>
            <MapContainer>
              <DeviceMap
                latitude={device.latitude}
                longitude={device.longitude}
                deviceName={device.name || `Marine Buoy ${device.id}`}
              />

              {device.latitude && device.longitude && (
                <TrackingInfo>
                  <TrackingLabel>Tracked position (last 24H)</TrackingLabel>
                  <TrackingValue>Position: {device.latitude.toFixed(4)}°, {device.longitude.toFixed(4)}°</TrackingValue>
                </TrackingInfo>
              )}

              <MapActions>
                <MapButton>
                  <Eye size={12} />
                  View weather
                </MapButton>
                <ExportButton>
                  <Download size={12} />
                  Export
                </ExportButton>
              </MapActions>
            </MapContainer>
          </SensorPanel>

          {/* System */}
          <SensorPanel>
            <PanelHeader>
              <PanelIcon>
                <Settings size={20} />
              </PanelIcon>
              <PanelTitle>System</PanelTitle>
            </PanelHeader>
            <SensorList>
              {systemData.map((sensor, index) => (
                <SensorRow key={index}>
                  <SensorLabel>{sensor.label}</SensorLabel>
                  <SensorValue status={sensor.status}>{sensor.value}</SensorValue>
                </SensorRow>
              ))}
            </SensorList>
          </SensorPanel>

          {/* Misc */}
          <SensorPanel>
            <PanelHeader>
              <PanelIcon>
                <Clock size={20} />
              </PanelIcon>
              <PanelTitle>Misc</PanelTitle>
            </PanelHeader>
            <SensorList>
              {miscData.map((sensor, index) => (
                <SensorRow key={index}>
                  <SensorLabel>{sensor.label}</SensorLabel>
                  <SensorValue status={sensor.status}>{sensor.value}</SensorValue>
                </SensorRow>
              ))}
            </SensorList>
          </SensorPanel>

          {/* Battery */}
          <SensorPanel>
            <PanelHeader>
              <PanelIcon>
                <Battery size={20} />
              </PanelIcon>
              <PanelTitle>Battery</PanelTitle>
            </PanelHeader>
            <SensorList>
              {batteryData.map((sensor, index) => (
                <SensorRow key={index}>
                  <SensorLabel>{sensor.label}</SensorLabel>
                  <SensorValue status={sensor.status}>{sensor.value}</SensorValue>
                </SensorRow>
              ))}
            </SensorList>
          </SensorPanel>

          {/* Geospatial */}
          <SensorPanel>
            <PanelHeader>
              <PanelIcon>
                <Globe size={20} />
              </PanelIcon>
              <PanelTitle>Geospatial</PanelTitle>
            </PanelHeader>
            <SensorList>
              {geospatialData.map((sensor, index) => (
                <SensorRow key={index}>
                  <SensorLabel>{sensor.label}</SensorLabel>
                  <SensorValue status={sensor.status}>{sensor.value}</SensorValue>
                </SensorRow>
              ))}
            </SensorList>
          </SensorPanel>

          {/* Communication */}
          <SensorPanel>
            <PanelHeader>
              <PanelIcon>
                <Wifi size={20} />
              </PanelIcon>
              <PanelTitle>Communication</PanelTitle>
            </PanelHeader>
            <SensorList>
              {communicationData.map((sensor, index) => (
                <SensorRow key={index}>
                  <SensorLabel>{sensor.label}</SensorLabel>
                  <SensorValue status={sensor.status}>{sensor.value}</SensorValue>
                </SensorRow>
              ))}
            </SensorList>
          </SensorPanel>
        </RightColumn>
      </SensorPanelsContainer>
    </>
  );
};

export default SensorPanels;
